import { ref, computed } from "vue";
import http from "@/helper/http";
import api from "@/helper/api";

const MAX_TASKS = 100;
const STORAGE_KEY = "ebm_background_tasks";
const AUTO_FETCH_DURATION = 2 * 60 * 1000; // 2 minutes
const FETCH_INTERVAL = 15 * 1000; // 15 seconds
const TERMINAL_TASK_STATUSES = new Set([
  "SUCCESS",
  "COMPLETED",
  "FAILURE",
  "FAILED",
  "ERROR",
]);
const RUNNING_TASK_STATUSES = new Set([
  "PENDING",
  "QUEUED",
  "STARTED",
  "RUNNING",
  "RETRY",
]);

export const isTerminalTaskStatus = (status) =>
  TERMINAL_TASK_STATUSES.has(String(status || "").toUpperCase());

// Reactive state
const tasks = ref([]);
const autoFetchIntervals = new Map(); // Map of taskId -> intervalId

// Task structure: { id, type, status, startedAt, lastFetchedAt, data, storeId }

export function useBackgroundTasks() {
  // Helper function to parse result and infer status
  // Backend returns Python dict string (e.g., "{'status': 'success'}"), not JSON
  const parseResultAndInferStatus = (resultInput) => {
    if (!resultInput) return { parsedResult: null, status: null };

    // If already an object, normalize and return
    if (typeof resultInput === "object") {
      const parsed = resultInput;
      formatDtFields(parsed);
      return { parsedResult: parsed, status: parsed.status || null };
    }

    // Handle string inputs
    try {
      // Try JSON first
      const parsed = JSON.parse(String(resultInput));
      formatDtFields(parsed);
      return { parsedResult: parsed, status: parsed.status || null };
    } catch (jsonError) {
      // Try to convert Python dict string to JSON
      try {
        let str = String(resultInput);
        // Convert Python dict string to JSON string
        // Handle UUID(...) -> string
        str = str.replace(/UUID\((['"][^'"]+['"])\)/g, (match, uuid) => uuid);

        // Convert Python boolean and null
        str = str.replace(/\bTrue\b/g, "true");
        str = str.replace(/\bFalse\b/g, "false");
        str = str.replace(/\bNone\b/g, "null");

        // Convert single quotes to double quotes
        str = str.replace(/'/g, '"');

        const parsed = JSON.parse(str);
        formatDtFields(parsed);
        return { parsedResult: parsed, status: parsed.status || null };
      } catch (pythonError) {
        // If both fail, return the original string
        return { parsedResult: String(resultInput), status: null };
      }
    }
  };

  // Helper function to format Dt fields (e.g., "20251027014147" -> "2025-10-27 01:41:47")
  const formatDtFields = (obj) => {
    if (!obj || typeof obj !== "object") return;

    // Recursively process objects and arrays
    if (Array.isArray(obj)) {
      obj.forEach((item) => formatDtFields(item));
    } else {
      Object.keys(obj).forEach((key) => {
        const value = obj[key];
        // If key ends with "Dt" and value is a string with date format
        if (key.endsWith("Dt") && typeof value === "string") {
          const dtPattern = /^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})$/;
          const match = value.match(dtPattern);
          if (match) {
            // Format as "YYYY-MM-DD HH:MM:SS"
            obj[
              key
            ] = `${match[1]}-${match[2]}-${match[3]} ${match[4]}:${match[5]}:${match[6]}`;
          } else {
            // Try shorter format like "20251017" -> "2025-10-17"
            const yearMonthDayPattern = /^(\d{4})(\d{2})(\d{2})$/;
            const ymdMatch = value.match(yearMonthDayPattern);
            if (ymdMatch) {
              obj[key] = `${ymdMatch[1]}-${ymdMatch[2]}-${ymdMatch[3]}`;
            } else {
              // Try year-month format like "202510"
              const yearMonthPattern = /^(\d{4})(\d{2})$/;
              const ymMatch = value.match(yearMonthPattern);
              if (ymMatch) {
                obj[key] = `${ymMatch[1]}-${ymMatch[2]}`;
              }
            }
          }
        } else if (typeof value === "object" && value !== null) {
          formatDtFields(value);
        }
      });
    }
  };

  // Load from localStorage on init
  const loadTasks = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        tasks.value = parsed.tasks || [];

        // Parse any results that are still strings (from old format)
        tasks.value.forEach((task) => {
          if (task.data?.result && typeof task.data.result === "string") {
            const { parsedResult } = parseResultAndInferStatus(
              task.data.result
            );
            task.data.result = parsedResult;
          }
        });

        // Restart auto-fetch for running tasks that are still within the 2-minute window
        tasks.value.forEach((task) => {
          if (isTaskRunning(task)) {
            if (isTaskStale(task)) {
              fetchTaskStatus(task.id);
            } else {
              startAutoFetch(task.id);
            }
          }
        });
      }
    } catch (error) {
      console.error(
        "Failed to load background tasks from localStorage:",
        error
      );
      tasks.value = [];
    }
  };

  // Save to localStorage
  const saveTasks = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ tasks: tasks.value }));
    } catch (error) {
      console.error("Failed to save background tasks to localStorage:", error);
    }
  };

  // Add new task
  const addTask = (taskId, type, data, storeId) => {
    const newTask = {
      id: taskId,
      type, // 'single_retry' | 'retry_all'
      status: "PENDING",
      startedAt: new Date().toISOString(),
      lastFetchedAt: new Date().toISOString(),
      data,
      storeId,
    };

    tasks.value.unshift(newTask);
    pruneOldTasks();
    saveTasks();
    startAutoFetch(taskId);

    return newTask;
  };

  // Fetch task status
  const fetchTaskStatus = async (taskId) => {
    try {
      const taskIndex = tasks.value.findIndex((t) => t.id === taskId);
      const existingTask = taskIndex === -1 ? null : tasks.value[taskIndex];
      const statusUrl =
        existingTask?.data?.statusUrl || api.background_task_status(taskId);
      const response = await http.get(
        statusUrl.startsWith("/api/") ? statusUrl.slice(4) : statusUrl,
      );
      const responseData = response.data.data;
      const taskData = responseData?.task || responseData;
      if (taskIndex !== -1) {
        const currentStatus = taskData.status;
        tasks.value[taskIndex].status = currentStatus;
        tasks.value[taskIndex].lastFetchedAt = new Date().toISOString();

        if (taskData.result) {
          // Parse the result and infer status if needed
          const { parsedResult, status: inferredStatus } =
            parseResultAndInferStatus(taskData.result);
          tasks.value[taskIndex].data.result = parsedResult;

          // If no status from API but we inferred one from result, use it
          if (!tasks.value[taskIndex].status && inferredStatus) {
            tasks.value[taskIndex].status = inferredStatus;
          } else if (!tasks.value[taskIndex].status) {
            // Log when status is missing after parsing result
            console.warn(
              "Background task missing status after parsing result",
              {
                taskId,
                result: taskData.result,
              }
            );
          }
          if (isTerminalTaskStatus(inferredStatus)) {
            stopAutoFetch(taskId);
          }
        }
        if (taskData.error) {
          tasks.value[taskIndex].data.error = taskData.error;
        }
        if (taskData.error_message) {
          tasks.value[taskIndex].data.error = taskData.error_message;
        }
        tasks.value[taskIndex].data.progress =
          taskData.progress ?? taskData.result?.progress ?? null;

        // Stop auto-fetch if task is completed or failed
        // Check both the API status and any inferred status from result
        const shouldStop = isTerminalTaskStatus(currentStatus);
        
        if (shouldStop) {
          stopAutoFetch(taskId);
        }

        saveTasks();
        return taskData;
      }
    } catch (error) {
      console.error(`Failed to fetch status for task ${taskId}:`, error);
      // Try to extract error message and status from response
      const responseData = error?.response?.data;
      const errorData = responseData?.data || responseData;
      const errorMessage =
        errorData?.error ||
        responseData?.error?.message ||
        error?.message ||
        "Failed to fetch task status";
      
      const taskIndex = tasks.value.findIndex((t) => t.id === taskId);
      if (taskIndex !== -1) {
        // Update task with error status if we can determine it
        // Check multiple possible locations for status
        const errorStatus = 
          errorData?.status ||
          responseData?.status ||
          (error?.response?.status >= 500 ? "FAILURE" : null);
        
        if (errorStatus) {
          tasks.value[taskIndex].status = errorStatus;
        } else {
          // Default to FAILURE if we can't determine status
          tasks.value[taskIndex].status = "FAILURE";
        }
        tasks.value[taskIndex].data.error = errorMessage;
        // Always stop polling on error
        stopAutoFetch(taskId);
        saveTasks();
      }
      return null;
    }
  };

  // Fetch all tasks (not just running ones - for refresh all functionality)
  const fetchAllRunningTasks = async () => {
    const allTaskIds = tasks.value.map((t) => t.id);
    if (allTaskIds.length === 0) {
      return;
    }

    try {
      const response = await http.post(api.background_tasks_batch, {
        task_ids: allTaskIds,
      });

      const taskResults = response.data.data.tasks;

      taskResults.forEach((taskResult) => {
        const taskIndex = tasks.value.findIndex(
          (t) => t.id === taskResult.task_id
        );
        if (taskIndex !== -1) {
          // Always update status from API
          if (taskResult.status) {
            tasks.value[taskIndex].status = taskResult.status;
          }

          tasks.value[taskIndex].lastFetchedAt = new Date().toISOString();

          if (taskResult.result) {
            // Parse the result and infer status if needed
            const { parsedResult, status: inferredStatus } =
              parseResultAndInferStatus(taskResult.result);
            tasks.value[taskIndex].data.result = parsedResult;

            // If no status from API but we inferred one from result, use it
            if (!tasks.value[taskIndex].status && inferredStatus) {
              tasks.value[taskIndex].status = inferredStatus;
            } else if (!tasks.value[taskIndex].status) {
              // Log when status is missing after parsing result
              console.warn(
                "Background task missing status after parsing result",
                {
                  taskId: taskResult.task_id,
                  result: taskResult.result,
                }
              );
            }
            if (isTerminalTaskStatus(inferredStatus)) {
              stopAutoFetch(taskResult.task_id);
            }
          }

          if (taskResult.error) {
            tasks.value[taskIndex].data.error = taskResult.error;
          }

          // Stop auto-fetch if task is completed or failed
          if (isTerminalTaskStatus(tasks.value[taskIndex].status)) {
            stopAutoFetch(taskResult.task_id);
          }
        }
      });

      saveTasks();
    } catch (error) {
      console.error("Failed to fetch all tasks:", error);
    }
  };

  // Remove old tasks when exceeding 100
  const pruneOldTasks = () => {
    if (tasks.value.length > MAX_TASKS) {
      // Sort by startedAt (newest first) and keep only the first MAX_TASKS
      tasks.value.sort((a, b) => new Date(b.startedAt) - new Date(a.startedAt));
      const tasksToRemove = tasks.value.slice(MAX_TASKS);

      // Stop auto-fetch for removed tasks
      tasksToRemove.forEach((task) => {
        stopAutoFetch(task.id);
      });

      tasks.value = tasks.value.slice(0, MAX_TASKS);
    }
  };

  // Auto-fetch logic with 2-minute limit
  const startAutoFetch = (taskId) => {
    // Clear existing interval if any
    stopAutoFetch(taskId);

    const intervalId = setInterval(async () => {
      const task = tasks.value.find((t) => t.id === taskId);
      if (!task) {
        stopAutoFetch(taskId);
        return;
      }

      // Enforce a hard cap on auto-fetch duration based on startedAt
      const nowMs = Date.now();
      const startedMs = new Date(task.startedAt).getTime();
      if (
        Number.isFinite(startedMs) &&
        nowMs - startedMs > AUTO_FETCH_DURATION
      ) {
        await fetchTaskStatus(taskId);
        stopAutoFetch(taskId);
        return;
      }

      // Stop if the task became stale or finished
      if (isTaskStale(task)) {
        await fetchTaskStatus(taskId);
        stopAutoFetch(taskId);
        return;
      }

      await fetchTaskStatus(taskId);
    }, FETCH_INTERVAL);

    autoFetchIntervals.set(taskId, intervalId);
  };

  const stopAutoFetch = (taskId) => {
    const intervalId = autoFetchIntervals.get(taskId);
    if (intervalId) {
      clearInterval(intervalId);
      autoFetchIntervals.delete(taskId);
    }
  };

  const removeTask = (taskId) => {
    const index = tasks.value.findIndex((task) => task.id === taskId);
    if (index === -1) {
      return;
    }
    stopAutoFetch(taskId);
    tasks.value.splice(index, 1);
    saveTasks();
  };

  const clearTasksForStore = (storeId, taskType = null) => {
    if (!storeId) {
      return;
    }
    const storeIdStr = String(storeId);
    const toRemove = tasks.value.filter(
      (task) => {
        const matchesStore = String(task.storeId) === storeIdStr;
        const matchesType = taskType ? task.type === taskType : true;
        return matchesStore && matchesType;
      }
    );
    toRemove.forEach((task) => stopAutoFetch(task.id));
    tasks.value = tasks.value.filter(
      (task) => {
        const matchesStore = String(task.storeId) === storeIdStr;
        const matchesType = taskType ? task.type === taskType : true;
        return !(matchesStore && matchesType);
      }
    );
    saveTasks();
  };

  const clearAllTasks = () => {
    tasks.value.forEach((task) => stopAutoFetch(task.id));
    tasks.value = [];
    saveTasks();
  };

  // Helper functions
  const isTaskRunning = (task) => {
    return RUNNING_TASK_STATUSES.has(
      String(task.status || "").toUpperCase(),
    );
  };

  const isTaskStale = (task) => {
    // Completed tasks should never be stale
    if (
      task.status?.toUpperCase() === "SUCCESS" ||
      task.status?.toUpperCase() === "FAILURE"
    ) {
      return false;
    }

    // If task has a result but no status, it's likely completed - don't mark as stale
    if (task.data?.result && !task.status) {
      return false;
    }

    // For running tasks, consider stale when we've been polling longer than the cap from start time
    const nowMs = Date.now();
    const startedMs = new Date(task.startedAt).getTime();
    if (Number.isFinite(startedMs)) {
      return nowMs - startedMs > AUTO_FETCH_DURATION;
    }
    return false;
  };

  // Get the effective task status, considering both Celery task status and result status
  const getEffectiveTaskStatus = (task) => {
    // First check the Celery task status (PENDING, STARTED, SUCCESS, FAILURE)
    const celeryStatus = task.status;

    // If task is completed (SUCCESS or FAILURE), check the result
    if (celeryStatus === "SUCCESS" || celeryStatus === "FAILURE") {
      // If there's a result, check if the operation itself had an error
      const resultStatus = task.data?.result?.status;
      if (resultStatus === "error") {
        return "ERROR";
      }
      if (resultStatus === "success") {
        return "COMPLETED";
      }
      if (resultStatus === "failed") {
        return "FAILED";
      }
    }

    // Return the Celery status as-is for running tasks
    return celeryStatus;
  };

  const getTaskDisplayName = (task) => {
    if (task.type === "single_retry") {
      return `Retry Transaction ${task.data.transactionUuid?.substring(
        0,
        8
      )}...`;
    } else if (task.type === "retry_all") {
      // Get store name from localStorage as fallback
      const stores = JSON.parse(localStorage.getItem("stores") || "[]");
      const store = stores.find((s) => s.id === task.storeId);
      const storeName = store?.name || `Store ${task.storeId}`;
      return `Retry All Failed (${storeName})`;
    } else if (task.type === "store_report") {
      const stores = JSON.parse(localStorage.getItem("stores") || "[]");
      const store = stores.find((s) => s.id === task.storeId);
      const storeName = store?.name || `Store ${task.storeId}`;
      const reportLabel =
        task.data?.reportLabel ||
        task.data?.reportType ||
        "Store Report";
      return `${reportLabel} (${storeName})`;
    } else if (
      task.type === "insights_explore" ||
      task.type === "insights_product" ||
      task.type === "insights_definition"
    ) {
      return task.data?.label || "Insights report";
    }
    return "Unknown Task";
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "PENDING":
      case "QUEUED":
        return "yellow";
      case "STARTED":
      case "RUNNING":
      case "RETRY":
        return "blue";
      case "SUCCESS":
      case "COMPLETED":
        return "green";
      case "FAILURE":
      case "FAILED":
      case "ERROR":
        return "red";
      default:
        return "gray";
    }
  };

  // Computed properties
  const runningTasks = computed(() =>
    tasks.value.filter((t) => isTaskRunning(t))
  );

  const completedTasks = computed(() =>
    tasks.value.filter((t) => !isTaskRunning(t))
  );

  const runningTasksCount = computed(() => runningTasks.value.length);

  // Initialize on first use
  if (tasks.value.length === 0) {
    loadTasks();
  }

  return {
    tasks,
    runningTasks,
    completedTasks,
    runningTasksCount,
    addTask,
    fetchTaskStatus,
    fetchAllRunningTasks,
    getTaskDisplayName,
    getStatusColor,
    getEffectiveTaskStatus,
    isTaskRunning,
    isTaskStale,
    loadTasks,
    saveTasks,
    removeTask,
    clearTasksForStore,
    clearAllTasks,
  };
}
